import _ from 'lodash';
import { COMPARISON_OPERATORS_SYMBOLS } from '@/toolSelection/investigate.constants';
import { validateGuid } from '@seeqdev/utils';
import type { OPERATOR } from '@/toolSelection/investigate.constants';
import type { ToolConfigParams } from '@/tools/tool.types';
import type { ValueSearchState } from '@/trendData/valueSearch.store';

const VERSION_TWO = 'V2';
export const VALUE_SEARCH_CONFIG_VERSION = 'V3';

/** An entry or exit condition as saved in a pre-V3 UIConfig's `limitsParams`. */
interface LegacyLimitCondition {
  operator?: string;
  value?: unknown;
  value2?: unknown;
  duration?: unknown;
  durationValue?: number;
  durationUnits?: string;
}

/** The parts of a saved Value Search UIConfig that the version migrations read and write. */
interface ValueSearchSavedConfig {
  version?: string;
  isSimple?: boolean;
  limitsParams?: {
    entryCondition?: LegacyLimitCondition;
    exitCondition?: LegacyLimitCondition;
  };
  [key: string]: unknown;
}

/**
 * Upgrades a saved Value Search UIConfig to the current (V3) shape so it can be loaded into the store for editing.
 * Returns a new object; the input is not mutated.
 */
export function migrateValueSearchConfig(config: ToolConfigParams): ToolConfigParams {
  const migrated = _.cloneDeep(config) as unknown as ValueSearchSavedConfig;
  if (migrated.version === VALUE_SEARCH_CONFIG_VERSION) {
    return config;
  }

  migrateLegacyDurations(migrated);
  if (migrated.version === undefined) {
    upgradeToV2(migrated);
    upgradeToV3(migrated);
  } else if (migrated.version === VERSION_TWO) {
    upgradeToV3(migrated);
  }

  return migrated as unknown as ToolConfigParams;
}

/** Remaps conditions from `{ durationValue, durationUnits }` to `{ duration: { value, units } }`. */
function migrateLegacyDurations(config: ValueSearchSavedConfig) {
  [config.limitsParams?.entryCondition, config.limitsParams?.exitCondition].forEach((condition) => {
    if (!condition) return;
    const { durationValue, durationUnits } = condition;
    if (durationValue !== undefined && durationUnits !== undefined) {
      condition.duration = { units: durationUnits, value: durationValue };
      delete condition.durationUnits;
      delete condition.durationValue;
    }
  });
}

/** V2 switched equality operators to their string-safe forms and defaulted existing searches to advanced mode. */
function upgradeToV2(config: ValueSearchSavedConfig) {
  config.version = VERSION_TWO;

  [config.limitsParams?.entryCondition, config.limitsParams?.exitCondition].forEach((condition) => {
    if (condition?.operator === '=') {
      condition.operator = '~=';
    } else if (condition?.operator === '!=') {
      condition.operator = '!~';
    }
  });

  if (config.isSimple === undefined) {
    config.isSimple = false;
  }
}

/** V3 flattened `limitsParams` into the separate simple and advanced fields the store holds. */
function upgradeToV3(config: ValueSearchSavedConfig) {
  config.version = VALUE_SEARCH_CONFIG_VERSION;
  const entry = config.limitsParams?.entryCondition;
  const exit = config.limitsParams?.exitCondition;

  if (config.isSimple) {
    config.simpleOperator = entry?.operator;
    config.simpleValue = entry?.value;
    config.simpleLowerValue = entry?.value2;
    config.minDuration = entry?.duration;
    config.mergeDuration = exit?.duration;
  } else {
    config.advancedEntryOperator = entry?.operator;
    config.advancedEntryValue = entry?.value;
    config.advancedEntryLowerValue = entry?.value2;
    config.advancedEntryDuration = entry?.duration;
    config.advancedExitOperator = exit?.operator;
    config.advancedExitValue = exit?.value;
    config.advancedExitDuration = exit?.duration;
  }

  delete config.limitsParams;
}

export function isOperatorBetween(operator: OPERATOR | undefined): boolean {
  return (
    operator === COMPARISON_OPERATORS_SYMBOLS.IS_BETWEEN || operator === COMPARISON_OPERATORS_SYMBOLS.IS_NOT_BETWEEN
  );
}

/**
 * The upper and lower inclusivity a simple search should default to when its operator changes to a between operator,
 * or `undefined` for any other operator.
 */
export function getSimpleBetweenInclusivity(
  operator: OPERATOR | undefined,
): { upper: OPERATOR; lower: OPERATOR } | undefined {
  if (operator === COMPARISON_OPERATORS_SYMBOLS.IS_BETWEEN) {
    return {
      upper: COMPARISON_OPERATORS_SYMBOLS.IS_LESS_THAN_OR_EQUAL_TO,
      lower: COMPARISON_OPERATORS_SYMBOLS.IS_GREATER_THAN_OR_EQUAL_TO,
    };
  }

  if (operator === COMPARISON_OPERATORS_SYMBOLS.IS_NOT_BETWEEN) {
    return {
      upper: COMPARISON_OPERATORS_SYMBOLS.IS_GREATER_THAN,
      lower: COMPARISON_OPERATORS_SYMBOLS.IS_LESS_THAN,
    };
  }

  return undefined;
}

/** True when the active form uses a between operator whose upper and lower values are the same. */
export function hasSameBetweenValues(
  state: Pick<
    ValueSearchState,
    | 'isSimple'
    | 'simpleValue'
    | 'simpleLowerValue'
    | 'simpleOperator'
    | 'advancedEntryValue'
    | 'advancedEntryLowerValue'
    | 'advancedEntryOperator'
  >,
): boolean {
  const [upperValue, lowerValue, operator] = state.isSimple
    ? [state.simpleValue, state.simpleLowerValue, state.simpleOperator]
    : [state.advancedEntryValue, state.advancedEntryLowerValue, state.advancedEntryOperator];

  return isOperatorBetween(operator) && !!upperValue && !!lowerValue && upperValue === lowerValue;
}

/**
 * True when a simple between search has a numeric lower limit greater than its upper limit. Limits that are signals
 * are not compared.
 */
export function isSimpleLowerBoundGreater(
  state: Pick<ValueSearchState, 'isSimple' | 'simpleOperator' | 'simpleValue' | 'simpleLowerValue'>,
): boolean {
  if (!state.isSimple || !isOperatorBetween(state.simpleOperator)) return false;

  const { simpleValue: upperValue, simpleLowerValue: lowerValue } = state;
  if (validateGuid(upperValue) || validateGuid(lowerValue)) {
    return false;
  }

  return !!upperValue && !!lowerValue && _.toNumber(upperValue) < _.toNumber(lowerValue);
}
